import { Button, Disclosure } from "@legal-500/legal500-design-system";
import { useCallback, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  contacts,
  contactRoles,
  offices,
  type Contact,
  type ContactRole,
  type MergeContactResponse,
  type Office,
} from "../../../api/addressBook.service";
import {
  AsyncSearchableSelect,
  type AsyncSelectOption,
} from "../../../components/Form/AsyncSearchableSelect";
import LoadingState from "../../../components/LoadingState";
import { PageHeader } from "../../../components/PageHeader";
import { ROUTES } from "../../../routes";
import { formatDateForApi } from "../../../utils/dateFormatter";
import styles from "../AddressBook.module.css";
import { WizardStepper } from "../components/WizardStepper";
import introStyles from "./WizardIntro.module.css";

type Step = "select" | "review" | "done";

const STEP_LABELS = ["Select Target", "Review", "Done"];

function stepperSteps(current: Step) {
  const order: Step[] = ["select", "review", "done"];
  const currentIndex = order.indexOf(current);
  return STEP_LABELS.map((label, i) => ({
    label,
    status:
      i < currentIndex
        ? ("complete" as const)
        : i === currentIndex
          ? ("active" as const)
          : ("pending" as const),
  }));
}

function isActiveRole(role: ContactRole): boolean {
  return role.endDate === null;
}

// Select Step Component
type SelectStepProps = {
  sourceContact: Contact;
  sourceId: string;
  targetContactId: string | null;
  effectiveDate: string;
  onTargetChange: (value: string | number | null) => void;
};

function SelectStep({
  sourceContact,
  sourceId,
  targetContactId,
  effectiveDate,
  onTargetChange,
}: SelectStepProps) {
  const handleSearch = useCallback(
    async (query: string): Promise<AsyncSelectOption[]> => {
      const res = await contacts.getAll({ search: query, limit: 25 });
      return res.data
        .filter(c => c.id !== sourceId)
        .map(c => ({
          value: c.id,
          label: c.name,
          description: c.email || "No email",
        }));
    },
    [sourceId],
  );

  return (
    <div className={styles.wizardFormContainer}>
      <div className={`${styles.section} ${styles.sectionAccent}`}>
        <h3 className={styles.sectionTitle}>Source Contact</h3>
        <div className={styles.reviewGrid}>
          <span className={styles.reviewLabel}>Name</span>
          <span className={styles.reviewValue}>{sourceContact.name}</span>

          <span className={styles.reviewLabel}>Email</span>
          <span className={styles.reviewValue}>
            {sourceContact.email || <span className={styles.emptyText}>No email</span>}
          </span>

          <span className={styles.reviewLabel}>Job Title</span>
          <span className={styles.reviewValue}>
            {sourceContact.jobTitle || <span className={styles.emptyText}>—</span>}
          </span>
        </div>
      </div>

      <div className={styles.section}>
        <AsyncSearchableSelect
          name="target-contact"
          label="Target Contact"
          required
          value={targetContactId}
          onChange={onTargetChange}
          onSearch={handleSearch}
          placeholder="Search for a contact..."
          helperText="Select the contact to merge into"
          debounceMs={300}
          minSearchLength={0}
        />
      </div>

      <div className={styles.section}>
        <div className={styles.formField}>
          <label className={styles.formLabel} htmlFor="effective-date">
            Effective Date
          </label>
          <input
            id="effective-date"
            type="date"
            className={styles.formInput}
            value={effectiveDate}
            disabled
          />
          <span className={styles.helperText}>Date the merge is recorded</span>
        </div>
      </div>
    </div>
  );
}

// Review Step Component
type ReviewStepProps = {
  sourceContact: Contact;
  targetContact: Contact;
  sourceRoles: ContactRole[];
  targetRoles: ContactRole[];
  loadingRoles: boolean;
  hasCollision: boolean;
  collisions: string[];
  error: string | null;
  getOfficeName: (officeId: string) => string;
};

function ReviewStep({
  sourceContact,
  targetContact,
  sourceRoles,
  targetRoles,
  loadingRoles,
  hasCollision,
  collisions,
  error,
  getOfficeName,
}: ReviewStepProps) {
  if (loadingRoles) {
    return (
      <div className={styles.wizardFormContainer}>
        <LoadingState message="Loading roles..." />
      </div>
    );
  }

  return (
    <div className={styles.wizardFormContainer}>
      <div className={styles.formGrid}>
        <div className={styles.section}>
          <h3 className={styles.sectionTitle}>Source: {sourceContact.name}</h3>
          <div className={styles.summaryRow}>
            <span>Active roles</span>
            <span
              className={`${styles.summaryBadge} ${sourceRoles.filter(isActiveRole).length === 0 ? styles.summaryBadgeZero : ""}`}
            >
              {sourceRoles.filter(isActiveRole).length}
            </span>
          </div>
          {sourceRoles.filter(isActiveRole).length > 0 && (
            <div className={`${styles.roleListReview} ${styles["mt-4"]}`}>
              {sourceRoles.filter(isActiveRole).map(role => (
                <div key={role.id} className={styles.roleItemReview}>
                  {role.roleType || "Role"} — {getOfficeName(role.officeId)}
                </div>
              ))}
            </div>
          )}
        </div>

        <div className={styles.section}>
          <h3 className={styles.sectionTitle}>Target: {targetContact.name}</h3>
          <div className={styles.summaryRow}>
            <span>Active roles</span>
            <span
              className={`${styles.summaryBadge} ${targetRoles.filter(isActiveRole).length === 0 ? styles.summaryBadgeZero : ""}`}
            >
              {targetRoles.filter(isActiveRole).length}
            </span>
          </div>
          {targetRoles.filter(isActiveRole).length > 0 && (
            <div className={`${styles.roleListReview} ${styles["mt-4"]}`}>
              {targetRoles.filter(isActiveRole).map(role => (
                <div key={role.id} className={styles.roleItemReview}>
                  {role.roleType || "Role"} — {getOfficeName(role.officeId)}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {hasCollision && (
        <div className={styles.collisionError}>
          <strong>Cannot merge:</strong> Both contacts have active roles at {collisions.join(", ")}.
          End one role before merging.
        </div>
      )}

      <div className={styles.warningBox}>
        <strong>Confirmation:</strong> All active roles from <strong>{sourceContact.name}</strong>{" "}
        will transfer to <strong>{targetContact.name}</strong>. {sourceContact.name} will be
        archived. This cannot be easily undone.
      </div>

      {error && <div className={styles.formAlert}>{error}</div>}
    </div>
  );
}

// Done Step Component
type DoneStepProps = {
  sourceContact: Contact;
  targetContact: Contact;
  rolesTransferred: number;
};

function DoneStep({ sourceContact, targetContact, rolesTransferred }: DoneStepProps) {
  return (
    <div className={styles.doneContainer}>
      <div className={styles.doneIconWrapper}>
        <span className={styles.doneIcon}>✓</span>
      </div>
      <h3 className={styles.doneTitle}>Merge Complete</h3>
      <p className={styles.doneMessage}>
        {rolesTransferred} role{rolesTransferred === 1 ? "" : "s"} transferred from{" "}
        <strong>{sourceContact.name}</strong> to <strong>{targetContact.name}</strong>
      </p>
    </div>
  );
}

export default function ContactMergeWizard() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const [step, setStep] = useState<Step>("select");
  const [sourceContact, setSourceContact] = useState<Contact | null>(null);
  const [targetContactId, setTargetContactId] = useState<string | null>(null);
  const [targetContact, setTargetContact] = useState<Contact | null>(null);
  const [effectiveDate] = useState(() => new Date().toISOString().split("T")[0]);
  const [sourceRoles, setSourceRoles] = useState<ContactRole[]>([]);
  const [targetRoles, setTargetRoles] = useState<ContactRole[]>([]);
  const [officesMap, setOfficesMap] = useState<Map<string, Office>>(new Map());
  const [loadingRoles, setLoadingRoles] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<MergeContactResponse | null>(null);
  const [loadingSource, setLoadingSource] = useState(true);

  useEffect(() => {
    if (!id) return;
    setLoadingSource(true);
    contacts
      .getById(id)
      .then(res => setSourceContact(res.data))
      .catch(() => setError("Failed to load source contact"))
      .finally(() => setLoadingSource(false));
  }, [id]);

  function handleTargetChange(value: string | number | null) {
    const contactId = value ? String(value) : null;
    setTargetContactId(contactId);
    if (contactId) {
      contacts
        .getById(contactId)
        .then(res => setTargetContact(res.data))
        .catch(() => setTargetContact(null));
    } else {
      setTargetContact(null);
    }
  }

  async function loadRolesForReview() {
    if (!sourceContact || !targetContact) return;
    setLoadingRoles(true);
    setError(null);
    try {
      const [sourceRolesRes, targetRolesRes] = await Promise.all([
        contactRoles.getAll({ contactId: sourceContact.id }),
        contactRoles.getAll({ contactId: targetContact.id }),
      ]);
      setSourceRoles(sourceRolesRes.data);
      setTargetRoles(targetRolesRes.data);

      const allOfficeIds = [
        ...new Set(
          [...sourceRolesRes.data, ...targetRolesRes.data].map(r => r.officeId).filter(Boolean),
        ),
      ];
      const officeResults = await Promise.allSettled(allOfficeIds.map(oid => offices.getById(oid)));
      const map = new Map<string, Office>();
      allOfficeIds.forEach((oid, i) => {
        const res = officeResults[i];
        if (res.status === "fulfilled") {
          map.set(oid, res.value.data);
        }
      });
      setOfficesMap(map);
    } catch {
      setError("Failed to load roles");
    } finally {
      setLoadingRoles(false);
    }
  }

  function goToReview() {
    setStep("review");
    loadRolesForReview();
  }

  async function handleConfirm() {
    if (!sourceContact || !targetContact) return;
    setSubmitting(true);
    setError(null);
    try {
      const res = await contacts.merge(sourceContact.id, {
        targetContactId: targetContact.id,
        effectiveDate: effectiveDate ? (formatDateForApi(effectiveDate) ?? null) : null,
      });
      setResult(res);
      setStep("done");
    } catch (err) {
      const message = err instanceof Error ? err.message : "Merge failed";
      setError(message);
    } finally {
      setSubmitting(false);
    }
  }

  function getOfficeName(officeId: string): string {
    const office = officesMap.get(officeId);
    return office?.name ?? officeId;
  }

  function detectCollisions(): string[] {
    const activeSourceOfficeIds = sourceRoles.filter(isActiveRole).map(r => r.officeId);
    const activeTargetOfficeIds = new Set(targetRoles.filter(isActiveRole).map(r => r.officeId));
    const collisions: string[] = [];
    for (const oid of activeSourceOfficeIds) {
      if (activeTargetOfficeIds.has(oid)) {
        collisions.push(getOfficeName(oid));
      }
    }
    return [...new Set(collisions)];
  }

  if (loadingSource) return <LoadingState message="Loading contact..." />;
  if (!sourceContact) {
    return (
      <div className={styles.wizardWrapper}>
        <PageHeader title="Merge Contact" />
        <div className={styles.formAlert}>{error || "Contact not found"}</div>
      </div>
    );
  }

  const collisions = step === "review" ? detectCollisions() : [];
  const hasCollision = collisions.length > 0;

  return (
    <div className={styles.wizardWrapper}>
      <PageHeader title="Merge Contact" subtitle={sourceContact.name} />

      <WizardStepper steps={stepperSteps(step)} />

      <div className={styles.wizardContent}>
        {step === "select" && (
          <div className={introStyles.intro}>
            <p className={introStyles.oneLiner}>Archives this contact into another.</p>
            <Disclosure summary="How it works">
              <p className={introStyles.detail}>
                All active roles on this contact are transferred to the target contact. This contact
                is then archived and no longer appears in active lists. Can be reversed later via
                Undo Merge.
              </p>
            </Disclosure>
          </div>
        )}

        {step === "select" && (
          <SelectStep
            sourceContact={sourceContact}
            sourceId={id!}
            targetContactId={targetContactId}
            effectiveDate={effectiveDate}
            onTargetChange={handleTargetChange}
          />
        )}

        {step === "review" && targetContact && (
          <ReviewStep
            sourceContact={sourceContact}
            targetContact={targetContact}
            sourceRoles={sourceRoles}
            targetRoles={targetRoles}
            loadingRoles={loadingRoles}
            hasCollision={hasCollision}
            collisions={collisions}
            error={error}
            getOfficeName={getOfficeName}
          />
        )}

        {step === "done" && targetContact && (
          <DoneStep
            sourceContact={sourceContact}
            targetContact={targetContact}
            rolesTransferred={result?.rolesTransferred ?? 0}
          />
        )}
      </div>

      <div className={styles.wizardActions}>
        {step === "select" && (
          <>
            <Button
              variant="outlined"
              color="default"
              onClick={() => navigate(ROUTES.addressBook.contactDetail(id!))}
            >
              Cancel
            </Button>
            <div className={styles.wizardActionsRight}>
              <Button
                variant="contained"
                color="primary"
                onClick={goToReview}
                disabled={!targetContact}
              >
                Next
              </Button>
            </div>
          </>
        )}

        {step === "review" && (
          <>
            <Button variant="outlined" color="default" onClick={() => setStep("select")}>
              Back
            </Button>
            <div className={styles.wizardActionsRight}>
              <Button
                variant="contained"
                color="primary"
                onClick={handleConfirm}
                disabled={submitting || hasCollision || loadingRoles}
              >
                {submitting ? "Merging..." : "Confirm"}
              </Button>
            </div>
          </>
        )}

        {step === "done" && (
          <div className={styles.wizardActionsRight}>
            <Button
              variant="outlined"
              color="default"
              onClick={() => navigate(ROUTES.addressBook.contacts)}
            >
              Back to Contacts
            </Button>
            <Button
              variant="contained"
              color="primary"
              onClick={() => navigate(ROUTES.addressBook.contactDetail(targetContact!.id))}
            >
              View {targetContact?.name}
            </Button>
          </div>
        )}
      </div>
    </div>
  );
}
